import { NoteTemplateFormat } from '@/shared/api/protocol-ts/model/dto_scribe_catalog_pb';

import type {
  DocumentSectionSpec,
  DocumentTemplateSummary,
  DocumentType,
  StoredSection,
  StripUnsupportedOptionsResult,
} from '../config';

import { parseDocumentType } from './normalizeDocumentType';

export const isProtocolTemplate = (
  type?: DocumentType | string | null,
): boolean => type === 'PROTOCOL' || type === 'protocol';

// Scribe scores the sections of an evaluation from 1 to 5, except the
// extended ones, which it generates one by one without a score.
export const isScoredSection = (
  type: DocumentType | string | null | undefined,
  section: Pick<DocumentSectionSpec, 'ProcessingType'>,
): boolean =>
  parseDocumentType(type) === 'EVALUATION' &&
  section.ProcessingType.trim().toLowerCase() !== 'extended';

export type DocumentTypeRules = {
  allowCategories: boolean;
  allowOptions: boolean;
  allowFields: boolean;
};

export const getDocumentTypeRules = (
  type?: DocumentType | string | null,
): DocumentTypeRules => {
  const protocol = isProtocolTemplate(type);

  return {
    allowCategories: true,
    allowOptions: protocol,
    allowFields: protocol,
  };
};

// Scribe compares a section's template type trimmed and ignoring case, and
// reads anything but `flexible` as strict.
export const isFlexibleTemplateType = (templateType?: string): boolean =>
  templateType?.trim().toLowerCase() === 'flexible';

// Scribe derives the format; lines of typed blocks are edited and filled
// through their own note view and editor.
export const isBlockNoteTemplate = (
  template: Pick<DocumentTemplateSummary, 'format'>,
): boolean => template.format === NoteTemplateFormat.NoteTemplateFormatBlocks;
/**
 * Drops section options that the document type cannot hold. Callers apply it
 * at every point where sections enter the form (load, type change, import) so
 * the state never carries options the editor does not render.
 */
export const stripUnsupportedOptions = (
  sections: StoredSection[],
  type?: DocumentType | string | null,
): StripUnsupportedOptionsResult => {
  if (getDocumentTypeRules(type).allowOptions) {
    return { sections, removedOptions: false };
  }

  const removedOptions = sections.some(
    ({ options }) => (options?.length ?? 0) > 0,
  );
  const strippedSections = sections.map((section) => {
    const { options, ...sectionWithoutOptions } = section;

    return options === undefined ? section : sectionWithoutOptions;
  });

  return { sections: strippedSections, removedOptions };
};

export const getDefaultSectionTemplateType = (
  type?: DocumentType | string | null,
): 'flexible' | 'strict' => (isProtocolTemplate(type) ? 'flexible' : 'strict');
